import React, { useCallback, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import MobileSelectDates from '../Common/MobileSelectDates'

import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import noop from 'lib/function/noop'
import { useUpdateTrip, useTrip } from 'tripPlanner/hooks/api/trip'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import * as Mapper from 'tripPlanner/mappers'

interface Props {
  tripId: string
  onDatesAdded?: (startDate: moment.Moment, endDate: moment.Moment) => void
}

/** Just like the version in EditTripDatesMC, except this one is for use with the Trip Planner useOpenModal() hook */
function EditTripDates(props: Props) {
  const { tripId, onDatesAdded = noop } = props

  const { data: trip } = useTrip({ tripId })

  const { startDate, endDate } = trip ?? {}

  const initialHasDates = useRef(!!(startDate && endDate)).current

  const { closeModal } = useModalCallbacks()
  const intl = useIntl()

  const datesRemovedMessage = intl.formatMessage({
    id: 'E/7bl5FzoU',
    defaultMessage: 'Dates removed',
    description: 'Snackbar message after removing dates from a trip',
  })

  const datesSavedMessage = intl.formatMessage({
    id: 'OnjmcRB8Re',
    defaultMessage: 'Dates saved',
    description: 'Snackbar message after saving dates to a trip',
  })

  const datesErrorMessage = intl.formatMessage({
    id: 'B5+2O8poH1',
    defaultMessage: 'Error while updating trip dates. Please try again.',
    description: 'Error message when trip date update fails',
  })

  const editDatesTitle = intl.formatMessage({
    id: '/B3toa6Gmx',
    defaultMessage: 'Edit dates',
    description: 'Modal title when editing existing trip dates',
  })

  const addDatesTitle = intl.formatMessage({
    id: 'xbjj8+MHo8',
    defaultMessage: 'Add dates',
    description: 'Modal title when adding dates to a trip for the first time',
  })

  const saveLabel = intl.formatMessage({
    id: 'TdUbSHUafj',
    defaultMessage: 'Save',
    description: 'Save button label in the trip dates modal',
  })

  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

  const { mutate, isLoading } = useUpdateTrip({
    onMutate: (): undefined => {
      setErrorMsg(undefined)
    },
    onSuccess: (updatedTripRaw) => {
      const trip = Mapper.fullTrip(updatedTripRaw)
      closeModal()
      const hasDates = !!(trip.startDate && trip.endDate)
      if (initialHasDates && !hasDates) {
        showSnackbar(datesRemovedMessage, 'success')
      } else if (hasDates) {
        showSnackbar(datesSavedMessage, 'success')
        onDatesAdded(trip.startDate!, trip.endDate!)
      }
    },
    onError: () => {
      setErrorMsg(datesErrorMessage)
    },
  })

  const updateTripDates = useCallback(
    (startDate: moment.Moment, endDate?: moment.Moment) => {
      mutate({
        tripId,
        startDate,
        endDate: endDate ?? startDate,
      })
    },
    [mutate, tripId],
  )

  const clearTripDates = useCallback(() => {
    mutate({
      tripId,
      startDate: null,
      endDate: null,
    })
  }, [mutate, tripId])

  return (
    <MobileSelectDates
      initialStartDate={startDate}
      initialEndDate={endDate}
      onChange={updateTripDates}
      onRemove={startDate ? clearTripDates : undefined}
      onCancel={closeModal}
      title={startDate ? editDatesTitle : addDatesTitle}
      doneLabel={startDate ? saveLabel : addDatesTitle}
      isSubmitting={isLoading}
      errorMsg={errorMsg}
    />
  )
}

export default EditTripDates
